import React from "react";
import { Switch } from "@/components/ui/switch";
import { NumericInput } from "@/components/shared/NumericInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, ArrowDown, Sparkles } from "lucide-react";

export interface ProcessConfig {
  processId: string;
  setupTimeMinutes?: number | null;
  speedPerHour?: number | null;
  runWastePercent?: number | null;
  setupWasteQty?: number | null;
  isOutsourced?: boolean;
  moq?: number | null;
  unitCost?: number | null;
  batchCost?: number | null;
}

interface Props {
  configs: ProcessConfig[];
  processLookup: { options: any[]; isLoading: boolean };
  productMaterials?: Array<{
    materialId: string;
    processId?: string | null;
    standardQuantity?: number | null;
    note?: string | null;
  }>;
  materialLookup?: { options: any[]; isLoading: boolean };
  onProductMaterialsChange?: (materials: any[]) => void;
  onChange: (configs: ProcessConfig[]) => void;
  onMove?: (index: number, direction: 'UP' | 'DOWN') => void;
  onNormalizeOrder?: () => void;
}

export function ProductProcessConfigsList({
  configs,
  processLookup,
  productMaterials = [],
  materialLookup,
  onProductMaterialsChange,
  onChange,
  onMove,
  onNormalizeOrder,
}: Props) {
  if (configs.length === 0) return null;

  const updateConfig = (idx: number, updates: Partial<ProcessConfig>) => {
    const next = [...configs];
    next[idx] = { ...next[idx], ...updates };
    onChange(next);
  };

  const handleAssignMaterial = (processId: string, materialId: string) => {
    if (!onProductMaterialsChange) return;
    const next = productMaterials.map((pm) => {
      if (pm.materialId === materialId) {
        return { ...pm, processId };
      }
      if (pm.processId === processId && pm.materialId !== materialId) {
        return { ...pm, processId: null };
      }
      return pm;
    });
    onProductMaterialsChange(next);
  };

  // Danh sách các vật tư đã chọn cho sản phẩm này
  const availableMaterials = (materialLookup?.options || []).filter((o) =>
    productMaterials.some((pm) => pm.materialId === o.id)
  );

  return (
    <div className="space-y-3 mt-4 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">
            Cấu hình Định mức Công đoạn & Chi phí Gia công
          </h4>
          <p className="text-xs text-slate-500">
            Thứ tự công đoạn quyết định trình tự sản xuất MES & bóc tách giá thành COGM
          </p>
        </div>
        {onNormalizeOrder && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onNormalizeOrder}
            className="h-7 text-xs gap-1.5 text-blue-700 bg-blue-50/50 hover:bg-blue-100 hover:text-blue-800 border-blue-200 shadow-none font-medium"
            title="Tự động sắp xếp lại theo thứ tự: In -> Cán màng -> UV -> Bồi sóng -> Bế -> Dán -> Đóng gói"
          >
            <Sparkles className="w-3.5 h-3.5 text-blue-600" /> Chuẩn Hóa Thứ Tự
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 border-b text-xs text-slate-600 font-semibold whitespace-nowrap">
            <tr>
              <th className="px-3 py-2 text-center w-20">Thứ tự</th>
              <th className="px-3 py-2 font-medium min-w-[150px]">Công đoạn</th>
              <th className="px-3 py-2 font-medium min-w-[190px]">Vật tư đầu vào</th>
              <th className="px-3 py-2 font-medium text-center">Gia công ngoài</th>
              <th className="px-3 py-2 font-medium min-w-[120px]">Phí bài / Setup (đ)</th>
              <th className="px-3 py-2 font-medium min-w-[110px]">Đơn giá chạy (đ)</th>
              <th className="px-3 py-2 font-medium min-w-[95px]">Bù hao bài (tờ)</th>
              <th className="px-3 py-2 font-medium min-w-[85px]">% Bù hao chạy</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {configs.map((config, idx) => {
              const procOpt = processLookup.options.find((o) => o.id === config.processId);
              const processName = procOpt?.label || config.processId;
              const procOriginal = procOpt?.original;
              const assignedMaterial = productMaterials.find(
                (pm) => pm.processId === config.processId
              );

              return (
                <tr
                  key={config.processId}
                  className="hover:bg-slate-50/70 bg-white transition-colors"
                >
                  {/* Cột Thứ tự & Nút di chuyển */}
                  <td className="px-2 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className="w-5 text-center font-mono text-xs font-semibold text-slate-500">
                        {idx + 1}
                      </span>
                      {onMove && (
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => onMove(idx, 'UP')}
                            className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-20 disabled:hover:text-slate-400 rounded"
                            title="Di chuyển lên trước"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            disabled={idx === configs.length - 1}
                            onClick={() => onMove(idx, 'DOWN')}
                            className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-20 disabled:hover:text-slate-400 rounded"
                            title="Di chuyển xuống sau"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Tên công đoạn */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[13px] text-slate-800 whitespace-nowrap">
                        {processName}
                      </span>
                      {procOriginal?.priceUnit && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-slate-200 text-slate-500 bg-slate-50 font-normal">
                          {procOriginal.priceUnit === 'PER_M2' ? 'm²' : procOriginal.priceUnit === 'PER_SHEET' ? 'tờ' : 'cái'}
                        </Badge>
                      )}
                    </div>
                  </td>

                  {/* Gán vật tư */}
                  <td className="px-3 py-2 min-w-[190px]">
                    <select
                      value={assignedMaterial?.materialId || ""}
                      onChange={(e) =>
                        handleAssignMaterial(config.processId, e.target.value)
                      }
                      className="h-8 text-xs border border-slate-200 rounded px-2 bg-white text-slate-800 w-full focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                    >
                      <option value="">— Chưa gắn vật tư —</option>
                      {availableMaterials.map((mat) => (
                        <option key={mat.id} value={mat.id}>
                          {mat.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Gia công ngoài */}
                  <td className="px-3 py-2 text-center">
                    <Switch
                      checked={config.isOutsourced || false}
                      onCheckedChange={(v) =>
                        updateConfig(idx, { isOutsourced: v })
                      }
                    />
                  </td>

                  {/* Phí bài tối thiểu (batchCost) */}
                  <td className="px-3 py-2 min-w-[120px]">
                    <NumericInput
                      value={config.batchCost ?? 0}
                      onChange={(v) => updateConfig(idx, { batchCost: v })}
                      className="h-8 w-full text-[13px]"
                      placeholder="0"
                    />
                  </td>

                  {/* Đơn giá chạy (unitCost) */}
                  <td className="px-3 py-2 min-w-[110px]">
                    <NumericInput
                      value={config.unitCost ?? 0}
                      onChange={(v) => updateConfig(idx, { unitCost: v })}
                      className="h-8 w-full text-[13px]"
                      placeholder="0"
                    />
                  </td>

                  {/* Bù hao bài setup (setupWasteQty) */}
                  <td className="px-3 py-2 min-w-[95px]">
                    <NumericInput
                      value={config.setupWasteQty ?? 0}
                      onChange={(v) => updateConfig(idx, { setupWasteQty: v })}
                      className="h-8 w-full text-[13px]"
                      placeholder="0"
                    />
                  </td>

                  {/* % Bù hao chạy máy (runWastePercent) */}
                  <td className="px-3 py-2 min-w-[85px]">
                    <NumericInput
                      value={config.runWastePercent ?? 0}
                      onChange={(v) => updateConfig(idx, { runWastePercent: v })}
                      className="h-8 w-full text-[13px]"
                      placeholder="0"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
